/* Basic building blocks shared by every screen. */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { humanize, initials, money } from '../lib/format';
import { Link } from '../app/router';
import { Icon, type IconName } from './icons';

type BtnVariant = 'primary' | 'secondary' | 'accent' | 'danger' | 'ghost' | 'default';

export function Button(props: {
  children?: ReactNode; variant?: BtnVariant; size?: 'sm' | 'lg'; icon?: IconName; onClick?: () => void;
  type?: 'button' | 'submit'; disabled?: boolean; loading?: boolean; block?: boolean; title?: string; form?: string;
}) {
  const { children, variant = 'default', size, icon, onClick, type = 'button', disabled, loading, block, title, form } = props;
  const cls = ['btn', variant !== 'default' ? variant : '', size ?? '', block ? 'block' : ''].filter(Boolean).join(' ');
  return (
    <button className={cls} type={type} onClick={onClick} disabled={disabled || loading} title={title} form={form}>
      {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon, title, onClick }: { icon: IconName; title: string; onClick?: () => void }) {
  return (
    <button className="icon-btn" type="button" title={title} aria-label={title} onClick={onClick}>
      <Icon name={icon} />
    </button>
  );
}

export function PageHeader({ title, sub, actions, crumbs }: {
  title: ReactNode; sub?: ReactNode; actions?: ReactNode; crumbs?: { label: string; to?: string }[];
}) {
  return (
    <div className="page-header">
      <div>
        {crumbs && (
          <div className="crumbs">
            {crumbs.map((c, i) => (
              <span key={i}>
                {c.to ? <Link to={c.to}>{c.label}</Link> : c.label}
                {i < crumbs.length - 1 ? ' / ' : ''}
              </span>
            ))}
          </div>
        )}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, pad = true, style }: {
  title?: ReactNode; actions?: ReactNode; children?: ReactNode; pad?: boolean; style?: CSSProperties;
}) {
  return (
    <div className="card" style={style}>
      {(title || actions) && (
        <div className="card-head">
          <h3>{title}</h3>
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      <div className={pad ? 'card-body' : ''}>{children}</div>
    </div>
  );
}

const TONES: Record<string, string> = {};
const setTone = (tone: string, words: string) => words.split(' ').forEach((w) => { TONES[w] = tone; });
setTone('success', 'active paid approved resolved success succeeded published clean ready completed returned warden_approved parent_approved closed match signed_off issued valid delivered sent loaded open_ok available verified done yes true current executed');
setTone('warning', 'pending open submitted partially_paid draft review calculated validated running staged in_progress assigned created uploaded profiled planned waived unread mismatch refund_pending checked_out p2 requested low queued scheduled skipped expiring');
setTone('danger', 'failed rejected overdue quarantined breached revoked cancelled expired error inactive blocked locked parent_rejected warden_rejected withdrawn rolled_back p1 no false lost pending_scan');
setTone('info', 'transferred exited info p3 p4 in_app email sms whatsapp');

export function toneOf(status: string | null | undefined): string {
  if (!status) return 'neutral';
  return TONES[String(status).toLowerCase()] ?? 'neutral';
}

export function StatusTag({ status, label, tone }: { status?: string | null | boolean; label?: string; tone?: string }) {
  const s = typeof status === 'boolean' ? (status ? 'yes' : 'no') : status ?? '';
  return <span className={`tag ${tone ?? toneOf(s)}`}>{label ?? humanize(String(s))}</span>;
}

export function Money({ value, whole, strong }: { value: unknown; whole?: boolean; strong?: boolean }) {
  const s = money(value, { whole });
  return strong ? <strong className="nowrap">{s}</strong> : <span className="nowrap">{s}</span>;
}

export function Stat({ label, value, delta, icon, tone = 'navy' }: {
  label: string; value: ReactNode; delta?: ReactNode; icon?: IconName; tone?: 'navy' | 'teal' | 'saffron';
}) {
  return (
    <div className={`card stat ${tone}`}>
      <div className="label">{icon && <Icon name={icon} size={15} />}{label}</div>
      <div className="value">{value}</div>
      {delta && <div className="delta">{delta}</div>}
    </div>
  );
}

export function Empty({ title = 'Nothing here yet', hint, icon = 'list', action }: {
  title?: string; hint?: ReactNode; icon?: IconName; action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon name={icon} />
      <div style={{ fontWeight: 600, color: 'var(--ink-700)' }}>{title}</div>
      {hint && <div className="small" style={{ marginTop: 4 }}>{hint}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

export function Loading({ label }: { label?: string }) {
  return (
    <div className="loading-block">
      <div className="row"><span className="spinner" /> <span className="muted">{label ?? 'Loading…'}</span></div>
    </div>
  );
}

export function Alert({ tone = 'info', children, title }: { tone?: 'info' | 'success' | 'warning' | 'danger'; children: ReactNode; title?: string }) {
  const icon: IconName = tone === 'danger' || tone === 'warning' ? 'alert' : tone === 'success' ? 'check' : 'bell';
  return (
    <div className={`alert ${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon name={icon} size={16} />
      <div>{title && <strong>{title}. </strong>}{children}</div>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="card card-body">
      <Alert tone="danger" title="Could not load">{error}</Alert>
      {onRetry && <div className="mt"><Button icon="refresh" onClick={onRetry}>Try again</Button></div>}
    </div>
  );
}

export function Tabs({ tabs, value, onChange }: { tabs: { key: string; label: ReactNode }[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} role="tab" aria-selected={value === t.key} className={value === t.key ? 'active' : ''} onClick={() => onChange(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function useTabs(initial: string): [string, (k: string) => void] {
  const [tab, setTab] = useState(initial);
  return [tab, setTab];
}

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === '' ? '—' : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Timeline({ items }: { items: { title: ReactNode; meta?: ReactNode; body?: ReactNode }[] }) {
  if (!items.length) return <Empty title="No history yet" icon="clock" />;
  return (
    <ul className="timeline">
      {items.map((it, i) => (
        <li key={i}>
          <div style={{ fontWeight: 600 }}>{it.title}</div>
          {it.meta && <div className="small muted">{it.meta}</div>}
          {it.body && <div className="small" style={{ marginTop: 2 }}>{it.body}</div>}
        </li>
      ))}
    </ul>
  );
}

export function Steps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="steps">
      {steps.map((s, i) => (
        <div key={s} className={`step ${i < current ? 'done' : i === current ? 'current' : ''}`}>
          {i < current ? <Icon name="check" size={14} /> : <span>{i + 1}</span>} {s}
        </div>
      ))}
    </div>
  );
}

export function Avatar({ name, large }: { name?: string | null; large?: boolean }) {
  return <div className={`avatar ${large ? 'lg' : ''}`}>{initials(name)}</div>;
}

export function Progress({ value, max = 100, tone }: { value: number; max?: number; tone?: 'warn' | 'bad' }) {
  const pct = Math.max(0, Math.min(100, max ? (value / max) * 100 : 0));
  return <div className={`progress ${tone ?? ''}`}><span style={{ width: `${pct}%` }} /></div>;
}

export function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row between"><h3>{title}</h3>{actions}</div>
      {children}
    </div>
  );
}

export function JsonDiff({ before, after }: { before?: unknown; after?: unknown }) {
  return (
    <div className="diff">
      <div><div className="small muted">Before</div><pre>{before ? JSON.stringify(before, null, 2) : '—'}</pre></div>
      <div><div className="small muted">After</div><pre>{after ? JSON.stringify(after, null, 2) : '—'}</pre></div>
    </div>
  );
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="chip">{children}</span>;
}
